import { imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Walker } from '../types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The night of lanterns (Caras prestadas 2, docs/SAGAS.md): the village crosses the clearing one by one along a
 * route. Every hero carries a lantern; under it the real ones cast a shadow and the impostors don't (the renderer
 * shows it). A standing hero who stays by an impostor for a moment tears its borrowed face off and a prestado comes
 * out; one that reaches the end of the route gets away. Lis, under a lantern, leaves her clue: she has a shadow.
 */

/** What the crowd leaves on the clue board (the battle's `clues.found`, without markers on the field). */
const clue = (s: BattleState, id: string, pid: string, x: number, y: number): void => {
  const c = (s.clues ??= { hold: [], found: [] });
  if (c.found.some((f) => f.id === id)) return;
  c.found.push({ id, pid, tick: s.tick });
  ev(s, { t: 'clue', id, pid, x, y });
};

const FACES_MISSING = ['woodcutter', 'daughter'];

export const crowdTick = (s: BattleState): void => {
  const r = s.rules?.crowd;
  if (!r || !s.started) return;
  const c = (s.crowd ??= { people: [], spawned: 0, unmasked: [], escaped: [], nextId: 1 });
  // One more comes out at the start of the route every so often.
  if (c.spawned < r.people && s.waveT >= c.spawned * r.every) {
    const start = r.route[0] as { x: number; y: number };
    const w: Walker = { id: c.nextId++, x: start.x, y: start.y, next: 1, facing: 0, near: 0 };
    const imp = r.impostors.find((i) => i.at === c.spawned);
    if (imp) w.face = imp.face;
    if (c.spawned === r.lis) w.lis = true;
    c.people.push(w);
    c.spawned++;
  }
  const keep: Walker[] = [];
  for (const w of c.people) {
    // Who stands by it: an impostor's face comes off; Lis, lit, shows her shadow.
    const by = s.heroes.find((h) => h.downTicks === 0 && withinRange(h.x, h.y, w.x, w.y, r.unmask));
    w.near = by ? w.near + 1 : 0;
    if (w.lis && s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, w.x, w.y, r.lantern))) clue(s, 'lisShadow', s.heroes.find((h) => h.downTicks === 0 && withinRange(h.x, h.y, w.x, w.y, r.lantern))?.pid ?? '', w.x, w.y);
    if (w.face && by && w.near >= r.ticks) {
      const e = spawnEnemy(s, 'prestado', 1, false, 0, w.x, w.y);
      if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
      c.unmasked.push(w.face);
      ev(s, { t: 'unmask', id: e.id, face: w.face, x: w.x, y: w.y });
      if (w.face === 'ebbe') clue(s, 'ebbeFace', by.pid, w.x, w.y);
      if (FACES_MISSING.every((f) => c.unmasked.includes(f))) clue(s, 'missingFaces', by.pid, w.x, w.y);
      continue;
    }
    // On along the route (they don't stop for the fight: the whole village is walking past).
    const to = r.route[w.next];
    if (to && stepToward(w, to.x, to.y, r.speed)) w.next++;
    if (w.next >= r.route.length) {
      if (w.face) c.escaped.push(w.face);
      continue;
    }
    keep.push(w);
  }
  c.people = keep;
};

/**
 * The night is over: lost if an impostor got away; won once all crossed, every face is off and the prestados are
 * down (whatever else is left on the field doesn't hold the night up).
 */
export const crowdResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.crowd;
  const c = s.crowd;
  if (!r || !c) return null;
  if (c.escaped.length) return 'lost';
  if (c.spawned < r.people || c.people.length) return null;
  if (c.unmasked.length < r.impostors.length) return null;
  if (s.enemies.some((e) => e.hp > 0 && e.kind === 'prestado')) return null;
  return 'won';
};
